import { useEffect, useRef } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { encodeRouteId, decodeRouteId } from "utils/routeIdCodec";

const STORAGE_PREFIX = "routeState:";

/**
 * Reads values that used to be exposed as URL params (e.g. /engagement/:id)
 * from React Router's location.state instead, so ids never show up in the
 * address bar.
 *
 * Falls back to sessionStorage so a page refresh (which clears location.state)
 * doesn't break the screen, as long as the user entered the flow normally in
 * this browser tab. Every time a value arrives via location.state it is
 * (re)persisted to sessionStorage so the latest value always wins.
 *
 * Pass { persistTo: "query" } to back the fallback with the URL's query
 * string instead of sessionStorage (survives refresh without using any
 * browser storage). The value is obfuscated (not encrypted — see
 * utils/routeIdCodec.js) before it's written to the URL so it isn't plainly
 * readable in the address bar. Backend authorization still applies to
 * whatever id is resolved.
 *
 * Usage:
 *   const { id } = useRouteState(["id"]);
 *   const { id, todoId } = useRouteState(["id", "todoId"]);
 *   const { id } = useRouteState(["id"], { persistTo: "query" });
 */
export const useRouteState = (keys = [], { persistTo = "sessionStorage" } = {}) => {
  const location = useLocation();
  const history = useHistory();
  const state = location.state || {};
  const queryParams = new URLSearchParams(location.search);

  const result = {};
  const toSync = {};

  keys.forEach((key) => {
    const storageKey = `${STORAGE_PREFIX}${key}`;
    const incoming = state[key];

    if (incoming !== undefined && incoming !== null && incoming !== "") {
      result[key] = incoming;

      if (persistTo === "query") {
        const encoded = encodeRouteId(incoming);
        if (queryParams.get(key) !== encoded) {
          toSync[key] = encoded;
        }
      } else {
        try {
          sessionStorage.setItem(storageKey, incoming);
        } catch (e) {
          // sessionStorage can fail (e.g. private mode); safe to ignore
        }
      }
    } else if (persistTo === "query") {
      const rawFromUrl = queryParams.get(key);
      result[key] = rawFromUrl ? decodeRouteId(rawFromUrl) : undefined;
    } else {
      let stored;
      try {
        stored = sessionStorage.getItem(storageKey);
      } catch (e) {
        stored = null;
      }
      result[key] = stored || undefined;
    }
  });

  const toSyncKey = JSON.stringify(toSync);
  const lastSyncedRef = useRef(null);

  useEffect(() => {
    if (persistTo !== "query") return;
    if (Object.keys(toSync).length === 0) return;
    if (lastSyncedRef.current === toSyncKey) return;
    lastSyncedRef.current = toSyncKey;

    const nextParams = new URLSearchParams(location.search);
    Object.entries(toSync).forEach(([key, encodedValue]) => {
      nextParams.set(key, encodedValue);
    });

    history.replace({
      pathname: location.pathname,
      search: nextParams.toString(),
      state,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toSyncKey, persistTo]);

  return result;
};

export default useRouteState;